import { isEqual } from "lodash";
import {
  PatchPayeeVendorInput,
  PayeeBaseDocumentInput,
  PatchPayeeAddressInput,
  PayeeVendorTaxDocumentInput,
  PayeeVendorTaxDocumentType,
} from "#graphql/supergraphTypes";
import { PayeeData } from "../../../../Context/PayeeContext";
import { PayeeBaseSupportingDocument, PayeeBaseAddress } from "../../../../types";
import { PayeeVendorTaxDocument, TaxDocumentType } from "../../../types";

/**
 * Converts TaxDocumentType to PayeeVendorTaxDocumentType for GraphQL mutations
 */
const convertTaxDocumentType = (documentType: TaxDocumentType): PayeeVendorTaxDocumentType => {
  switch (documentType) {
    case TaxDocumentType.US_TAX:
      return "US_TAX";
    case TaxDocumentType.NON_US_TAX:
      return "NON_US_TAX";
    default:
      return "US_TAX";
  }
};

/**
 * Transforms PayeeBaseSupportingDocument to PayeeBaseDocumentInput format
 */
const transformSupportingDocumentForMutation = (
  doc: PayeeBaseSupportingDocument,
): PayeeBaseDocumentInput => ({
  name: doc.name,
  size: doc.size,
  s3ObjectKey: doc.s3ObjectKey,
  ...(doc.contentType && { contentType: doc.contentType }),
});

/**
 * Transforms PayeeVendorTaxDocument to PayeeVendorTaxDocumentInput format
 */
const transformTaxDocumentForMutation = (
  doc: PayeeVendorTaxDocument,
): PayeeVendorTaxDocumentInput => ({
  name: doc.name,
  size: doc.size,
  s3ObjectKey: doc.s3ObjectKey,
  documentType: convertTaxDocumentType(doc.documentType),
  ...(doc.contentType && { contentType: doc.contentType }),
});

/**
 * Transforms PayeeBaseAddress to PatchPayeeAddressInput format
 */
const transformAddressForMutation = (address: PayeeBaseAddress): PatchPayeeAddressInput => ({
  ...(address.street && { street: address.street }),
  ...(address.street2 && { street2: address.street2 }),
  ...(address.city && { city: address.city }),
  ...(address.postalCode && { postalCode: address.postalCode }),
  ...(address.stateId && { stateId: address.stateId }),
  ...(address.countryId && { countryId: address.countryId }),
  ...(address.region && { region: address.region }),
});

/**
 * Compares two arrays of supporting documents for equality
 */
const areSupportingDocumentsEqual = (
  current?: PayeeBaseSupportingDocument[],
  original?: PayeeBaseSupportingDocument[],
): boolean => {
  if (!current && !original) return true;
  if (!current || !original) return false;
  if (current.length !== original.length) return false;

  // Transform both arrays to mutation format for comparison
  const currentTransformed = current.map(transformSupportingDocumentForMutation);
  const originalTransformed = original.map(transformSupportingDocumentForMutation);

  return isEqual(currentTransformed, originalTransformed);
};

/**
 * Compares two arrays of tax documents for equality
 */
const areTaxDocumentsEqual = (
  current?: PayeeVendorTaxDocument[],
  original?: PayeeVendorTaxDocument[],
): boolean => {
  if (!current && !original) return true;
  if (!current || !original) return false;
  if (current.length !== original.length) return false;

  // Transform both arrays to mutation format for comparison
  const currentTransformed = current.map(transformTaxDocumentForMutation);
  const originalTransformed = original.map(transformTaxDocumentForMutation);

  return isEqual(currentTransformed, originalTransformed);
};

/**
 * Compares two addresses for equality
 */
const areAddressesEqual = (
  current?: PayeeBaseAddress,
  original?: PayeeBaseAddress,
): boolean => {
  if (!current && !original) return true;
  if (!current || !original) return false;

  // Transform both addresses to mutation format for comparison
  const currentTransformed = transformAddressForMutation(current);
  const originalTransformed = transformAddressForMutation(original);

  return isEqual(currentTransformed, originalTransformed);
};

/**
 * Generates a patch diff object for Vendor payee mutations that only includes changed fields.
 * Compares the current payee state with the original payee data from context.
 *
 * @param currentPayeeData - The current payee data from context
 * @param originalPayeeData - The original payee data before any edits
 * @returns PatchPayeeVendorInput with only changed fields, or null if no changes
 * @throws Error if immutable fields (payeeId, arenaId) have changed
 */
export const generatePayeePatchDiff = (
  currentPayeeData: PayeeData,
  originalPayeeData: PayeeData,
): Omit<PatchPayeeVendorInput, "payeeId"> | null => {
  // Validate that immutable fields haven't changed
  if (currentPayeeData.payeeId !== originalPayeeData.payeeId) {
    throw new Error("PayeeId cannot be changed during edit operations");
  }

  if (currentPayeeData.arenaId !== originalPayeeData.arenaId) {
    throw new Error("ArenaId cannot be changed during edit operations");
  }

  const changes: Partial<Omit<PatchPayeeVendorInput, "payeeId">> = {};
  let hasChanges = false;

  // Compare payee name
  if (currentPayeeData.payeeName !== originalPayeeData.payeeName) {
    changes.name = currentPayeeData.payeeName;
    hasChanges = true;
  }

  // Compare payment preference ID
  const currentPaymentPrefId = currentPayeeData.paymentPref?.id;
  const originalPaymentPrefId = originalPayeeData.paymentPref?.id;

  if (currentPaymentPrefId !== originalPaymentPrefId) {
    changes.paymentPrefId = currentPaymentPrefId;
    hasChanges = true;
  }

  // Compare invoices (supporting documents for vendor)
  if (!areSupportingDocumentsEqual(currentPayeeData.invoices, originalPayeeData.invoices)) {
    changes.invoices = currentPayeeData.invoices?.map(transformSupportingDocumentForMutation);
    hasChanges = true;
  }

  // Compare payee address
  if (!areAddressesEqual(currentPayeeData.payeeAddress, originalPayeeData.payeeAddress)) {
    changes.address = currentPayeeData.payeeAddress
      ? transformAddressForMutation(currentPayeeData.payeeAddress)
      : undefined;
    hasChanges = true;
  }

  // Compare tax country ID
  if (currentPayeeData.taxCountryId !== originalPayeeData.taxCountryId) {
    changes.taxIdCountry = currentPayeeData.taxCountryId;
    changes.taxIdUnencrypted = currentPayeeData.taxId;
    hasChanges = true;
  }

  // Compare tax ID
  if (currentPayeeData.taxId !== originalPayeeData.taxId) {
    changes.taxIdUnencrypted = currentPayeeData.taxId;
    changes.taxIdCountry = currentPayeeData.taxCountryId;
    hasChanges = true;
  }

  // Compare form 1099 required
  if (currentPayeeData.form1099Required !== originalPayeeData.form1099Required) {
    changes.form1099Required = currentPayeeData.form1099Required == "yes" ? true : false;
    hasChanges = true;
  }

  // Compare tax documents
  if (!areTaxDocumentsEqual(currentPayeeData.taxDocuments, originalPayeeData.taxDocuments)) {
    changes.taxDocuments = currentPayeeData.taxDocuments?.map(transformTaxDocumentForMutation);
    hasChanges = true;
  }

  return hasChanges ? changes : null;
};
